/**
 * Phase 7.40 — Family-groups query hooks.
 *
 * Thin React Query wrapper around `api.family.*`. Used by the
 * PlanningModeToggle (to decide visibility) and the header invitations
 * bell. The single-group invariant from 7.40.1 means `groups[0]` is the
 * canonical active membership when present.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type FamilyGroup, type FamilyInvitation } from '@/lib/api';

export const familyKeys = {
  all: ['family'] as const,
  groups: () => [...familyKeys.all, 'groups'] as const,
  invitations: () => [...familyKeys.all, 'invitations'] as const,
};

export function useFamilyGroups(): UseQueryResult<FamilyGroup[], Error> {
  return useQuery({
    queryKey: familyKeys.groups(),
    queryFn: async () => {
      const res = await api.family.list();
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useFamilyInvitations(): UseQueryResult<FamilyInvitation[], Error> {
  return useQuery({
    queryKey: familyKeys.invitations(),
    // Phase 7.40.8 — polls every 60s so the header bell badge stays fresh
    // without a websocket. Background tabs throttle this naturally.
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await api.family.invitations();
      return res.data;
    },
  });
}
